/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useMemo, forwardRef } from 'react';
import { useSuperAppContext } from '@airasia-rn/ui';
import {
  SocialFeedPage,
  SocialFeedPageV4,
  SocialFeedPageContext,
  type SocialFeedPageContextType,
  type SocialFeedPageProps,
  type SocialFeedPageV4Ref,
  type openModuleProps,
  type backHandlerProps,
} from '@airasia-rn/social';

export type SocialFeedWidgetProps = SocialFeedPageProps & {
  openModule?: (data: openModuleProps) => void;
  backHandler?: (params?: backHandlerProps) => void;
};

export function SocialFeedWidget(props: SocialFeedWidgetProps) {
  const uiContext = useSuperAppContext();

  const contextValue: SocialFeedPageContextType = useMemo(
    () => ({
      ...uiContext,
      recommendationHashKey: props.recommendationHashKey,
      openModule: props.openModule ?? uiContext?.openModule,
      backHandler: props.backHandler ?? uiContext?.backHandler,
      runFeedAnimation: props.runFeedAnimation,
      maxPostsPerSection: props.maxPostsPerSection ?? null,
      maxSections: props.maxSections ?? null,
      maxChips: props.maxChips ?? null,
      onSectionHeaderClick: props.onSectionHeaderClick,
      onFeedItemClick: props.onFeedItemClick,
      onFooterChipClick: props.onFooterChipClick,
      onRetry: props.onRetry,
      onEmptyState: props.onEmptyState,
      onNoInternetState: props.onNoInternetState,
      onResultState: props.onResultState,
      onLoadingState: props.onLoadingState,
      onAnimationStart: props.onAnimationStart,
      onAnimationEnd: props.onAnimationEnd,
    }),
    [props, uiContext],
  );

  return (
    <SocialFeedPageContext.Provider value={contextValue}>
      <SocialFeedPage
        recommendationHashKey={props.recommendationHashKey}
        runFeedAnimation={props.runFeedAnimation}
        onSectionHeaderClick={props.onSectionHeaderClick}
        onFeedItemClick={props.onFeedItemClick}
        onFooterChipClick={props.onFooterChipClick}
        onRetry={props.onRetry}
        onEmptyState={props.onEmptyState}
        onNoInternetState={props.onNoInternetState}
        onResultState={props.onResultState}
        onLoadingState={props.onLoadingState}
        onAnimationStart={props.onAnimationStart}
        onAnimationEnd={props.onAnimationEnd}
      />
    </SocialFeedPageContext.Provider>
  );
}

export const SocialFeedWidgetV4 = forwardRef<
  SocialFeedPageV4Ref,
  SocialFeedWidgetProps
>(({ ...props }, ref) => {
  const uiContext = useSuperAppContext();

  const contextValue: SocialFeedPageContextType = useMemo(
    () => ({
      ...uiContext,
      recommendationHashKey: props.recommendationHashKey,
      openModule: props.openModule ?? uiContext?.openModule,
      backHandler: props.backHandler ?? uiContext?.backHandler,
      runFeedAnimation: props.runFeedAnimation,
      maxPostsPerSection: props.maxPostsPerSection ?? null,
      maxSections: props.maxSections ?? null,
      maxChips: props.maxChips ?? null,
      onSectionHeaderClick: props.onSectionHeaderClick,
      onFeedItemClick: props.onFeedItemClick,
      onFooterChipClick: props.onFooterChipClick,
      onRetry: props.onRetry,
      onEmptyState: props.onEmptyState,
      onNoInternetState: props.onNoInternetState,
      onResultState: props.onResultState,
      onLoadingState: props.onLoadingState,
      onAnimationStart: props.onAnimationStart,
      onAnimationEnd: props.onAnimationEnd,
    }),
    [props, uiContext],
  );

  return (
    <SocialFeedPageContext.Provider value={contextValue}>
      <SocialFeedPageV4
        ref={ref}
        recommendationHashKey={props.recommendationHashKey}
        runFeedAnimation={props.runFeedAnimation}
        onSectionHeaderClick={props.onSectionHeaderClick}
        onFeedItemClick={props.onFeedItemClick}
        onFooterChipClick={props.onFooterChipClick}
        onRetry={props.onRetry}
        onEmptyState={props.onEmptyState}
        onNoInternetState={props.onNoInternetState}
        onResultState={props.onResultState}
        onLoadingState={props.onLoadingState}
        onAnimationStart={props.onAnimationStart}
        onAnimationEnd={props.onAnimationEnd}
        showCategoryChips={props.showCategoryChips}
      />
    </SocialFeedPageContext.Provider>
  );
});

SocialFeedWidgetV4.displayName = 'SocialFeedWidgetV4';
